import React from 'react'
import { cn } from '@/lib/utils'
import { Copy, Check } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

interface CopyButtonProps {
  value?: string
  valueHtml?: string
  className?: string
  title?: string
}

export function CopyButton({ value, valueHtml, className, title }: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false)

  const onCopy = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      if (valueHtml && 'clipboard' in navigator && 'ClipboardItem' in window) {
        const blobHtml = new Blob([valueHtml], { type: 'text/html' })
        const plain = (value != null ? value : (() => {
          try {
            const doc = new DOMParser().parseFromString(valueHtml || '', 'text/html')
            return doc.body.textContent || ''
          } catch { return '' }
        })()) as string
        const blobText = new Blob([plain || ''], { type: 'text/plain' })
        // @ts-ignore - ClipboardItem typing
        const item = new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })
        await navigator.clipboard.write([item])
      } else {
        await navigator.clipboard.writeText((valueHtml ? (() => {
          try {
            const doc = new DOMParser().parseFromString(valueHtml || '', 'text/html')
            return doc.body.textContent || ''
          } catch { return '' }
        })() : value) ?? '')
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch {
      // ignore
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center justify-center rounded p-1.5 text-xs hover:bg-muted transition-colors',
            className
          )}
          onClick={onCopy}
          aria-label={title || 'Copy'}
          type="button"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="px-2 py-1 text-xs">
        {title || 'Copy'}
      </TooltipContent>
    </Tooltip>
  )
}

export default CopyButton
